/**
 * AdvertiserWidgetsPage — manage per-advertiser dashboard widgets.
 * Pick an advertiser id, see the ordered layout, add/remove widgets,
 * toggle enabled, and nudge positions (up/down → POST /reorder).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useCreateWidget,
	useDeleteWidget,
	useReorderWidgets,
	useUpdateWidget,
	useWidgetLayout,
} from "./hooks";
import type { WidgetKind, WidgetView, WidgetWidth } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const KINDS: WidgetKind[] = ["kpi", "chart", "table"];
const WIDTHS: WidgetWidth[] = [1, 2, 3, 4];

function moveInArray<T>(arr: T[], from: number, to: number): T[] {
	if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) {
		return arr;
	}
	const copy = arr.slice();
	const [item] = copy.splice(from, 1);
	copy.splice(to, 0, item);
	return copy;
}

export function AdvertiserWidgetsPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [pendingAdvertiserId, setPendingAdvertiserId] = useState("");

	const layoutQuery = useWidgetLayout(advertiserId);
	const createMut = useCreateWidget();
	const updateMut = useUpdateWidget();
	const deleteMut = useDeleteWidget(advertiserId);
	const reorderMut = useReorderWidgets();

	const [kind, setKind] = useState<WidgetKind>("kpi");
	const [title, setTitle] = useState("");
	const [width, setWidth] = useState<WidgetWidth>(1);

	const widgets = useMemo<WidgetView[]>(
		() => layoutQuery.data?.widgets ?? [],
		[layoutQuery.data],
	);

	const handleChooseAdvertiser = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = pendingAdvertiserId.trim();
		if (!trimmed) return;
		setAdvertiserId(trimmed);
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!advertiserId || !title.trim()) return;
		createMut.mutate(
			{
				advertiser_id: advertiserId,
				kind,
				title: title.trim(),
				width,
				enabled: true,
			},
			{
				onSuccess: () => {
					setTitle("");
					setWidth(1);
					setKind("kpi");
				},
			},
		);
	};

	const handleToggle = (w: WidgetView) => {
		updateMut.mutate({
			id: w.id,
			body: { enabled: !w.enabled },
		});
	};

	const handleMove = (idx: number, delta: -1 | 1) => {
		const target = idx + delta;
		const next = moveInArray(widgets, idx, target);
		if (next === widgets) return;
		reorderMut.mutate({
			advertiser_id: advertiserId,
			ids: next.map((w) => w.id),
		});
	};

	return (
		<main
			data-testid="advertiser-widgets-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-widgets
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser Widgets
				</h1>
				<p className="text-sm text-zinc-600">
					Pick an advertiser to manage its dashboard layout — KPI tiles,
					charts, and tables.
				</p>
			</header>

			<section className={CARD} aria-label="Pick advertiser">
				<h2 className="text-base font-semibold text-zinc-900">
					Advertiser
				</h2>
				<form
					onSubmit={handleChooseAdvertiser}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="advertiser-widgets-advertiser-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="advertiser_id"
						value={pendingAdvertiserId}
						onChange={(e) => setPendingAdvertiserId(e.currentTarget.value)}
						data-testid="advertiser-widgets-advertiser-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!pendingAdvertiserId.trim()}
						data-testid="advertiser-widgets-advertiser-submit"
					>
						Load layout
					</button>
				</form>
				{advertiserId ? (
					<p
						className="mt-2 text-xs text-zinc-500"
						data-testid="advertiser-widgets-advertiser-active"
					>
						Viewing: <span className="font-mono">{advertiserId}</span>
					</p>
				) : null}
			</section>

			{advertiserId ? (
				<>
					<section className={CARD} aria-label="Create widget">
						<h2 className="text-base font-semibold text-zinc-900">
							Add widget
						</h2>
						<form
							onSubmit={handleCreate}
							className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto_auto]"
							data-testid="advertiser-widgets-create-form"
						>
							<input
								type="text"
								className={INPUT}
								placeholder="Title"
								value={title}
								onChange={(e) => setTitle(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="advertiser-widgets-title-input"
							/>
							<select
								className={SELECT}
								value={kind}
								onChange={(e) =>
									setKind(e.currentTarget.value as WidgetKind)
								}
								disabled={createMut.isPending}
								data-testid="advertiser-widgets-kind-select"
							>
								{KINDS.map((k) => (
									<option key={k} value={k}>
										{k}
									</option>
								))}
							</select>
							<select
								className={SELECT}
								value={width}
								onChange={(e) =>
									setWidth(
										Number(e.currentTarget.value) as WidgetWidth,
									)
								}
								disabled={createMut.isPending}
								data-testid="advertiser-widgets-width-select"
							>
								{WIDTHS.map((w) => (
									<option key={w} value={w}>
										w={w}
									</option>
								))}
							</select>
							<button
								type="submit"
								className={BTN}
								disabled={createMut.isPending || !title.trim()}
								data-testid="advertiser-widgets-submit"
							>
								{createMut.isPending ? "Saving." : "Add"}
							</button>
						</form>
						{createMut.error ? (
							<p className="mt-2 text-xs text-red-600" role="alert">
								{createMut.error.message}
							</p>
						) : null}
					</section>

					<section className={CARD} aria-label="Widget layout">
						<div className="flex items-center justify-between">
							<h2 className="text-base font-semibold text-zinc-900">
								Layout
							</h2>
							<span className="text-xs text-zinc-500">
								{widgets.length} widget{widgets.length === 1 ? "" : "s"}
							</span>
						</div>
						{layoutQuery.isLoading ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="advertiser-widgets-loading"
							>
								Loading.
							</p>
						) : layoutQuery.error ? (
							<p className="mt-3 text-sm text-red-600" role="alert">
								{layoutQuery.error.message}
							</p>
						) : widgets.length === 0 ? (
							<p
								className="mt-3 text-sm text-zinc-500"
								data-testid="advertiser-widgets-empty"
							>
								No widgets yet.
							</p>
						) : (
							<ul
								className="mt-3 divide-y divide-zinc-200"
								data-testid="advertiser-widgets-list"
							>
								{widgets.map((w, idx) => (
									<li
										key={w.id}
										className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
										data-testid={`advertiser-widgets-row-${w.id}`}
									>
										<div className="flex flex-col gap-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium text-zinc-900">
													{w.title}
												</span>
												<span className="rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-zinc-600">
													{w.kind}
												</span>
												<span className="rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-zinc-600">
													w={w.width}
												</span>
												{!w.enabled ? (
													<span
														className="rounded-[4px] border border-zinc-200 bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-zinc-500"
														data-testid={`advertiser-widgets-disabled-${w.id}`}
													>
														off
													</span>
												) : null}
											</div>
											<span className="font-mono text-[11px] text-zinc-500">
												{w.id} · pos {w.position}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => handleMove(idx, -1)}
												disabled={
													idx === 0 || reorderMut.isPending
												}
												data-testid={`advertiser-widgets-up-${w.id}`}
											>
												Up
											</button>
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => handleMove(idx, 1)}
												disabled={
													idx === widgets.length - 1 ||
													reorderMut.isPending
												}
												data-testid={`advertiser-widgets-down-${w.id}`}
											>
												Down
											</button>
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => handleToggle(w)}
												disabled={updateMut.isPending}
												data-testid={`advertiser-widgets-toggle-${w.id}`}
											>
												{w.enabled ? "Disable" : "Enable"}
											</button>
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => deleteMut.mutate(w.id)}
												disabled={deleteMut.isPending}
												data-testid={`advertiser-widgets-delete-${w.id}`}
											>
												Delete
											</button>
										</div>
									</li>
								))}
							</ul>
						)}
					</section>
				</>
			) : null}
		</main>
	);
}

export default AdvertiserWidgetsPage;
